<template>
  <div class="bg-black h-full">
    <div class="bg-black flex h-full">
      <div class="flex flex-col flex-grow relative">
        <header
          class="
            header-student header
            absolute
            left-0
            right-0
            top-0
            overflow-visible
            opacity-90
            text-white
            px-3
            z-30
            transition-all
            bg-header-grey
            duration-300
            border border-t-0 border-strong-grey
            rounded-b-xl
            ease-in-out
          "
          :class="{ active: headerActive }"
        >
          <SessionHeader :toggleHeader="toggleHeader" :headerActive="headerActive" />
        </header>
        <main class="relative overflow-hidden" v-if="sessionStore.mediaPermissionError">
          <PermissionsNotGrantedOverlay />
        </main>
        <main
          class="relative overflow-hidden"
          v-else-if="sessionStore.userIsObserver && !sessionStore.observerInteracted"
        >
          <ObserverInteractionOverlay />
        </main>
        <main
          class="bg-black flex items-center justify-center overflow-hidden"
          v-else-if="!sessionStore.phasesDataInitialized"
        >
          <svg
            class="animate-spin -ml-1 mr-3 h-6 w-6 text-gray-150"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </main>
        <template v-else>
          <main v-if="sessionStore.isGroupCallStarted" class="overflow-hidden">
            <GroupCallView @show-header="toggleHeader($event)" />
          </main>
          <main
            class="relative px-2 py-4 overflow-hidden"
            v-else-if="sessionStore.isConsultationCallStarted"
          >
            <ConsultationCallView @show-header="toggleHeader($event)" />
          </main>
          <main
            v-else-if="sessionStore.isSessionEnded"
            class="flex justify-center items-center overflow-hidden"
          >
            <SessionEnd :isClinician="false" :sessionId="session.unique_id" />
          </main>
          <main
            v-else
            class="relative py-4 text-center text-white w-full overflow-hidden content-center"
          >
            <PermissionsNotGrantedOverlay v-if="sessionStore.mediaPermissionError" />
            <section
              class="mb-4 flex justify-center items-center gap-5"
              :class="{
                'h-full flex-col gap-2 flex justify-center': sessionStore.userIsObserver,
              }"
            >
              <h1 class="prose-xl font-bold leading-5">Waiting for clinician to start</h1>
              <h2 class="flex justify-center items-center space-x-2">
                <span
                  class="
                    inline-flex
                    justify-center
                    items-center
                    prose-sm
                    font-bold
                    w-10
                    h-10
                    border-2
                    rounded-full
                  "
                >
                  {{ sessionStore.currentPhase }}
                </span>
                <span class="prose-2xl font-medium">
                  {{ sessionStore.currentPhaseData?.['title'] }}
                </span>
              </h2>
            </section>
            <section
              id="preview-section"
              class="inline-flex w-auto justify-center px-6 content-center"
              v-if="!sessionStore.userIsObserver"
            >
              <StreamPlayerLocal
                class="flex w-1/2 rounded-l-lg camera bg-vice-light-grey"
                :class="{ 'w-1/3': sessionStore.currentPhase === VICE_SESSION_PHASES.DEBRIEF }"
                :isMinimizable="false"
              />
              <IntroductionPrecallView
                class="flex w-1/2 preview-right text-left"
                v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION"
              />
              <ConsultationPrecallView
                class="flex w-1/2 preview-right text-left"
                v-else-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION"
              />
              <DebriefPrecallView
                class="flex-col w-1/3 preview-right"
                v-else-if="sessionStore.currentPhase === VICE_SESSION_PHASES.DEBRIEF"
              />
            </section>
          </main>
        </template>

        <!-- Footer -->
        <SessionFooter
          :jitsiRoom="jitsiRoom"
          :defaultDevices="[]"
          @handleRaiseMyHand="handleRaiseMyHand"
        />
      </div>
      <SessionSidebar />
    </div>
  </div>
  <BrowserCheckModal></BrowserCheckModal>
  <TwoFactorAuthModal></TwoFactorAuthModal>
</template>

<script>
import { ref, onUnmounted, watch, onMounted } from 'vue';
import { Intercom } from '@intercom/messenger-js-sdk';
import { useSetupSessionData } from '@/Composables/useSetupSessionData';
import {
  VICE_SESSION_PHASES,
  DISPLAY_NAME,
  SESSION_CALL_HELPERS,
  DEVICE_NAME,
  INTERCOM_APP_ID,
} from '@/Utils/vice-constants';
import IntroductionPrecallView from '@/Components/Participant/IntroductionPrecallView';
import ConsultationPrecallView from '@/Components/Participant/ConsultationPrecallView';
import DebriefPrecallView from '@/Components/Participant/DebriefPrecallView.vue';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import SessionHeader from '@/Components/Session/Header/SessionHeader.vue';
import SessionFooter from '@/Components/Session/Footer/SessionFooter.vue';
import SessionEnd from '@/Components/Modals/SessionEnd.vue';
import useSendDeviceMessage from '@/Composables/useSendDeviceMessage';
import SessionSidebar from '@/Components/Session/Sidebar/SessionSidebar.vue';
import JitsiService from '@/Services/JitsiService';
import useUpdateMediaDevicesList from '@/Composables/useUpdateMediaDevicesList';
import useJitsiCommands from '@/Composables/useJitsiCommands';
import PermissionsNotGrantedOverlay from '@/Components/Session/Overlays/PermissionsNotGrantedOverlay';
import ObserverInteractionOverlay from '@/Components/Session/Overlays/ObserverInteractionOverlay';
import useSessionHandlers from '@/Composables/useSessionHandlers';
import GroupCallView from '@/Components/Participant/GroupCallView.vue';
import ConsultationCallView from '@/Components/Participant/ConsultationCallView.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import SessionAnalyticsService from '@/Services/SessionAnalyticsService';
import BrowserCheckModal from '@/Components/Modals/BrowserCheckModal.vue';
import TwoFactorAuthModal from '@/Components/Modals/TwoFactorAuthModal.vue';

import useTourManager from '@/Composables/useTourManager';
import { userSessionTourSteps } from '@/Data/tourSteps';

const userSessionTour = useTourManager('userSessionTour', userSessionTourSteps);

export default {
  props: {
    errors: Object,
    jetstream: Object,
    user: Object,
    errorBags: Object,
    isAdmin: Boolean,
    isClinician: Boolean,
    session: Object,
    clinicianRole: String,
    status: String,
    platformSettings: Object,
    sessionLifetime: String,
    defaultCourseUrl: String,
  },
  components: {
    SessionEnd,
    SessionFooter,
    SessionHeader,
    GroupCallView,
    SessionSidebar,
    BrowserCheckModal,
    StreamPlayerLocal,
    DebriefPrecallView,
    TwoFactorAuthModal,
    ConsultationCallView,
    ConsultationPrecallView,
    IntroductionPrecallView,
    ObserverInteractionOverlay,
    PermissionsNotGrantedOverlay,
  },

  setup(props) {
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const headerActive = ref(true);
    const systemInfoSent = ref(false);

    const sendJitsiCommand = useJitsiCommands(jitsiRoom);
    const { sessionStore, pageProps } = useSetupSessionData();
    const { commands, events, helpers } = useSessionHandlers();
    const sessionId = props.session.unique_id.replace(/-/g, '');
    const localStreamingDevicesStore = useLocalStreamingDevicesStore();

    sessionStore.jitsiCommands = sendJitsiCommand;
    sessionStore.deviceMessageSender = useSendDeviceMessage();

    // Initialize the device update listener
    useUpdateMediaDevicesList();

    // Handle page exit to log user leaving
    const handlePageExit = () => {
      const formData = new FormData();
      formData.append('session_id', sessionStore.sessionUniqueId);
      formData.append('user_id', sessionStore.authUser.unique_id);
      navigator.sendBeacon(`/api/session-analytics/user-leaving`, formData);
    };

    window.addEventListener('beforeunload', handlePageExit);

    /**
     * Start a new chat thread for the session or get existing messages
     */
    onMounted(() => {
      document.title = props.session.title;
      sessionStore.checkForPermissions();

      helpers[SESSION_CALL_HELPERS.HANDLE_NAVIGATION](true);

      JitsiService()
        .makeConnection(sessionId, events, commands)
        .then((jitsiInstance) => {
          jitsiRoom.value = jitsiInstance.room;
          disconnect.value = jitsiInstance.disconnect;
        });

      helpers[SESSION_CALL_HELPERS.INITIALIZE_CHAT_MESSAGES]();
      setTimeout(() => {
        userSessionTour.startTour();
      }, 200);

      Intercom({
        app_id: INTERCOM_APP_ID,
        user_id: sessionStore.authUser.unique_id,
        name: sessionStore.authUser.name,
        email: sessionStore.authUser.email,
        user_hash: sessionStore.authUser.user_hash,
        horizontal_padding: 20,
        vertical_padding: 20,
        hide_default_launcher: true,
      });
    });

    watch(jitsiRoom, (newJitsiRoom) => {
      if (newJitsiRoom) {
        initializeRoom();
      }
    });

    // Send user system data when session page loaded (device initialized)
    watch(
      () => localStreamingDevicesStore.devicesInitialized,
      (value) => {
        if (!systemInfoSent.value) {
          if (value && !props.isClinician) {
            SessionAnalyticsService.logUserSystemData(
              pageProps,
              localStreamingDevicesStore.activeDeviceLabels[DEVICE_NAME.CAMERA],
            );
            systemInfoSent.value = true;
          }
        }
      },
    );

    onUnmounted(async () => {
      window.removeEventListener('beforeunload', handlePageExit);

      try {
        await disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    const initializeRoom = () => {
      jitsiRoom.value.setDisplayName(DISPLAY_NAME.NAVIGATION_USER);
      jitsiRoom.value.setLocalParticipantProperty('isNavigationUser', true);
      jitsiRoom.value.join();
      sessionStore.navigatorUserId = jitsiRoom.value.myUserId();
    };

    const toggleHeader = (state = null) => {
      if (typeof state === 'boolean') {
        headerActive.value = state;
        return;
      }

      headerActive.value = !headerActive.value;
    };

    return {
      sessionId,
      jitsiRoom,
      sessionStore,
      headerActive,
      toggleHeader,
      BrowserCheckModal,
      VICE_SESSION_PHASES,
      handleRaiseMyHand: sendJitsiCommand.toggleRaiseHand,
    };
  },
};
</script>

<style lang="scss" scoped>
.header {
  height: 96px;
}

.header-student {
  top: 0;
  left: 0;
  width: auto;
  color: #fff;
  transform: translateY(-85%);
}

.header-student.active {
  transform: translateY(0);
}

.hover-gradient {
  background: transparent;
}

.hover-gradient:hover {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
}

main {
  height: 100%;

  .grid.gap-0 {
    width: calc((100vh - 205px) * 1.777777);
    margin: 0 auto;
  }
}

#preview-section {
  height: 332px;

  .camera {
    min-width: 430px;
  }

  .preview-right {
    min-width: 430px;
  }
}

.footer {
  height: 96px;
}

.min-w-main {
  min-width: 1064px;
}
</style>
